import {
  AlignLeftIcon,
  ArrowRotateLeftIcon,
  Button,
  Divider,
  EstimatorAvatar,
  HtmlContent,
  makeStyles,
  StickyNoteIcon,
  Typography,
  useMediaQuery,
  useTranslation
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { Editor, useClientOptions, useUsers } from '@paintscout/ui-business'
import {
  formatPhoneNumber,
  getDetails,
  getJobAddress,
  getObjectLabels,
  getQuoteOptions,
  getTotals,
  setDetails,
  setQuoteOptions
} from '@paintscout/util/builder'
import { formatName, getActivityColor } from '@paintscout/util'
import { isEmpty } from '@paintscout/util/util'
import addressFormat from 'address-format'
import classnames from 'classnames'
import AddIcon from '@material-ui/icons/Add'
// Crew Note / Product Description use the design's own note-sticky + align-left glyphs (see
// @paintscout/ui icons); `SquareFoot` still stands in for the mock's ruler.
import LocationOnIcon from '@material-ui/icons/LocationOnOutlined'
import PersonIcon from '@material-ui/icons/PersonOutline'
import SquareFootIcon from '@material-ui/icons/SquareFootOutlined'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOffOutlined'
import type { QuoteDocument } from 'paintscout'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useQuote } from '../context'
import { translateStructuredWorkOrderContent } from '../Quote/views/WorkOrderView/WorkOrderCard/translateStructuredWorkOrderContent'
import { useTranslatedNoteContent } from '../Quote/views/WorkOrderView/useTranslatedNoteContent'

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    const border = `1px solid ${neutral.P90}`
    return {
      // Two-column summary: notes/products on the left, the crew/job facts on the right, split by a
      // full-height rule. Stacks on small screens.
      root: {
        display: 'flex',
        borderTop: border,
        [theme.breakpoints.down('sm')]: {
          flexDirection: 'column'
        },
        // Phones drop the two-column idea entirely: one stack of divided rows, each a muted label over
        // its value. The columns keep their DOM order, so the row borders (not the columns' padding and
        // gaps) do the separating — hence the zeroed padding/gap below.
        [theme.breakpoints.down('xs')]: {
          flexDirection: 'column'
        }
      },
      left: {
        // Equal halves, like the mock's two 500px columns.
        flex: '1 1 0',
        minWidth: 0,
        padding: theme.spacing(3.5),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(5),
        [theme.breakpoints.down('xs')]: {
          padding: 0,
          gap: 0
        }
      },
      // Tinted panel (the prototype's #f7f8fa) so the job facts read as a distinct column from the
      // notes on the white left side.
      right: {
        flex: '1 1 0',
        minWidth: 0,
        padding: theme.spacing(3.5),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
        [theme.breakpoints.down('xs')]: {
          padding: 0,
          gap: 0
        }
      },
      vDivider: {
        width: 1,
        alignSelf: 'stretch',
        backgroundColor: neutral.P90,
        [theme.breakpoints.down('sm')]: {
          width: 'auto',
          height: 1
        },
        // Redundant on phones — every row draws its own top border.
        [theme.breakpoints.down('xs')]: {
          display: 'none'
        }
      },
      // Job Address sits above Sales Rep on phones (the mock's order) without reordering the desktop DOM.
      jobAddressRow: {
        [theme.breakpoints.down('xs')]: {
          order: -1
        }
      },
      // --- left column blocks ---
      block: {
        // `lg` (8px) matches the mock's note hover target exactly, as do its 8/4 insets.
        borderRadius: theme.borderRadius.lg,
        margin: theme.spacing(-0.5, -1),
        padding: theme.spacing(0.5, 1),
        // Phones: a full-bleed divided row rather than an inset hover pill.
        [theme.breakpoints.down('xs')]: {
          borderRadius: 0,
          borderTop: border,
          margin: 0,
          padding: theme.spacing(1.5, 2)
        }
      },
      blockClickable: {
        cursor: 'pointer',
        transition: 'background-color 150ms ease',
        '&:hover': { backgroundColor: neutral.P96 }
      },
      blockLabelRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        marginBottom: theme.spacing(1.5),
        [theme.breakpoints.down('xs')]: {
          marginBottom: theme.spacing(0.75)
        }
      },
      blockIcon: {
        fontSize: 16,
        color: neutral.P60
      },
      blockLabel: {
        color: neutral.P60,
        fontSize: theme.typography.pxToRem(14)
      },
      noteText: {
        color: neutral.P40,
        fontSize: theme.typography.pxToRem(14),
        lineHeight: 'normal',
        maxWidth: 390,
        whiteSpace: 'pre-wrap',
        [theme.breakpoints.down('xs')]: {
          maxWidth: 'none'
        }
      },
      // Phones move each note's glyph off the label and down beside the text, so every row in the stack
      // reads the same way: muted label, then icon + value.
      mobileValueRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1)
      },
      mobileValueIcon: {
        fontSize: 16,
        color: neutral.P60,
        flexShrink: 0,
        marginTop: 2
      },
      mobileValueText: {
        minWidth: 0
      },
      // Empty-state affordance in place of the em dash: "+ Add Crew Note". The block around it is
      // already the click target that opens the editor, so this is presentation only.
      addNote: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        color: neutral.P60,
        fontSize: theme.typography.pxToRem(13)
      },
      addNoteIcon: {
        fontSize: 16
      },
      // Inline editor takeover for a note block (Crew Note / Products). Relative so the Products
      // "Reset" button can sit in the top-right corner over the editor toolbar. It draws no border of
      // its own: the Editor already shows a blue focus ring, and a wrapper outline put a second grey
      // box around it while typing (ENG-1486). The padding keeps that ring clear of the block text.
      editorWrap: {
        position: 'relative',
        borderRadius: theme.borderRadius.xl,
        padding: theme.spacing(0.5, 0.5, 0.75)
      },
      resetButton: {
        position: 'absolute',
        top: theme.spacing(0.5),
        right: theme.spacing(0.5),
        zIndex: 1,
        textTransform: 'none'
      },
      subLabel: {
        color: neutral.P60,
        fontSize: theme.typography.pxToRem(14),
        marginBottom: theme.spacing(0.5)
      },
      // Product Description + Additional Work read as one story, so they sit a block-gap apart rather
      // than the column's wider gap.
      productsGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
          gap: 0
        }
      },
      // --- right column rows ---
      infoRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(2),
        // Phones stack the label over its value, in the same divided row the notes use. A 90px label
        // column beside the value leaves too little room for an address or an email on a phone.
        [theme.breakpoints.down('xs')]: {
          flexDirection: 'column',
          gap: theme.spacing(0.75),
          borderTop: border,
          padding: theme.spacing(1.5, 2)
        }
      },
      // Total hours keeps its label and value on one line (value right-aligned) — it's one short number,
      // and the mock leads the stack with it.
      hoursRow: {
        [theme.breakpoints.down('xs')]: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: theme.spacing(2),
          // It leads the mobile stack, and `root` already draws the line above it — its own top border
          // would double that.
          borderTop: 'none'
        }
      },
      infoLabel: {
        flex: '0 0 90px',
        color: neutral.P60,
        fontSize: theme.typography.pxToRem(14),
        paddingTop: 1,
        [theme.breakpoints.down('xs')]: {
          flex: '0 0 auto',
          fontSize: theme.typography.pxToRem(13),
          paddingTop: 0
        }
      },
      infoValue: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        color: neutral.P35,
        fontSize: theme.typography.pxToRem(14),
        // Once the label sits above it, the value owns the row's full width.
        [theme.breakpoints.down('xs')]: {
          width: '100%'
        }
      },
      // The mock sets the sales rep's name a shade lighter than the address/contact values.
      salesRepName: {
        color: neutral.P40
      },
      infoValueRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75)
      },
      // Contact block: the hide control appears on hover (and hides in print), so the row reads clean
      // until you go looking for it.
      contactBlock: {
        flex: 1,
        minWidth: 0,
        borderRadius: theme.borderRadius.lg,
        margin: theme.spacing(-0.5),
        padding: theme.spacing(0.5),
        '&:hover $contactHideButton': { opacity: 1 }
      },
      contactHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1)
      },
      contactHideButton: {
        display: 'flex',
        alignItems: 'center',
        padding: 2,
        border: 'none',
        background: 'none',
        borderRadius: theme.borderRadius.md,
        cursor: 'pointer',
        opacity: 0,
        transition: 'opacity 120ms ease, background-color 120ms ease',
        flexShrink: 0,
        color: neutral.P70,
        '&:hover': { backgroundColor: neutral.P94 },
        '&:focus-visible': { opacity: 1 },
        '& svg': { fontSize: 16 },
        '@media print': { display: 'none' }
      },
      // Stands in for the email/phone lines while they're hidden; click to bring them back.
      contactHidden: {
        color: neutral.P75,
        fontStyle: 'italic',
        fontSize: theme.typography.pxToRem(14),
        paddingLeft: theme.spacing(2.5),
        border: 'none',
        background: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        '&:hover': { color: neutral.P50 }
      },
      contactHiddenStatic: {
        color: neutral.P75,
        fontStyle: 'italic',
        fontSize: theme.typography.pxToRem(14),
        paddingLeft: theme.spacing(2.5)
      },
      contactLine: {
        paddingLeft: theme.spacing(2.5)
      },
      infoIcon: {
        fontSize: 16,
        color: neutral.P50,
        flexShrink: 0
      },
      hoursValue: {
        display: 'flex',
        alignItems: 'baseline',
        gap: theme.spacing(0.75)
      },
      hoursNumber: {
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(18),
        color: neutral.P35,
        // Tabular digits so every digit is exactly 1ch wide — that's what lets the input below size
        // itself in `ch` and occupy the same width as this text, keeping "hrs" still on click.
        fontVariantNumeric: 'tabular-nums'
      },
      // Click-to-edit affordance on the total-hours number: a subtle rounded hover wash hinting it's
      // editable (matches the inline-editable header cards).
      hoursClickable: {
        cursor: 'pointer',
        // `lg` (8px), same soft target as the note blocks.
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing(0, 0.5),
        margin: theme.spacing(0, -0.5),
        '&:hover': {
          backgroundColor: neutral.P96
        }
      },
      // The inline editor: inherits the hours look with no chrome of its own — the mock's field is
      // bare text, so no fill or border. Spinners hidden so it stays a clean number field.
      hoursInput: {
        font: 'inherit',
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(18),
        color: neutral.P35,
        border: 'none',
        outline: 'none',
        backgroundColor: 'transparent',
        padding: 0,
        minWidth: 0,
        fontVariantNumeric: 'tabular-nums',
        '-moz-appearance': 'textfield',
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
          '-webkit-appearance': 'none',
          margin: 0
        }
      },
      hoursUnit: {
        color: neutral.P60,
        fontSize: theme.typography.pxToRem(14)
      },
      // Shown only when total hours have been manually overridden: a segmented pill flagging the
      // adjustment ("Adjusted") joined to a Reset action that reverts to the computed default.
      adjustedPill: {
        marginLeft: 'auto',
        flexShrink: 0,
        alignSelf: 'center',
        display: 'flex',
        alignItems: 'stretch',
        border,
        borderRadius: theme.borderRadius.full,
        backgroundColor: neutral.White,
        overflow: 'hidden',
        '@media print': { display: 'none' }
      },
      adjustedLabel: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0.25, 1, 0.25, 1.5),
        color: neutral.P50,
        fontSize: theme.typography.pxToRem(12),
        whiteSpace: 'nowrap'
      },
      adjustedResetButton: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.25, 1.5, 0.25, 1),
        border: 'none',
        borderLeft: border,
        background: 'none',
        font: 'inherit',
        fontSize: theme.typography.pxToRem(12),
        color: neutral.P50,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        transition: 'background-color 150ms ease',
        '&:hover': { backgroundColor: neutral.P96 }
      },
      adjustedResetIcon: {
        fontSize: 15
      },
      avatar: {
        flexShrink: 0
      },
      // Dimensions: inline label/value pairs, separated by thin vertical rules.
      dimensions: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1.5)
      },
      dimensionItem: {
        display: 'flex',
        alignItems: 'baseline',
        gap: theme.spacing(1)
      },
      dimensionDivider: {
        width: 1,
        height: 10,
        borderRadius: theme.borderRadius.full,
        backgroundColor: neutral.P90,
        flexShrink: 0
      },
      dimensionLabel: {
        color: neutral.P50,
        fontSize: theme.typography.pxToRem(14)
      },
      dimensionValue: {
        fontWeight: 500,
        color: neutral.P35,
        fontSize: theme.typography.pxToRem(14)
      }
    }
  },
  { name: 'WorkOrderSummaryNext' }
)

export interface WorkOrderSummaryNextHandle {
  /**
   * Apply an open inline Products edit onto `baseQuote` and close the editor, returning the updated
   * quote (or null when nothing was open / nothing changed). Lets the owner fold the edit into its own
   * quote update instead of racing it — e.g. "Hide all products", which unmounts the Products block.
   */
  commitOpenProducts: (baseQuote: QuoteDocument) => QuoteDocument | null
}

/**
 * The Work Order summary panel: Crew Note / Products (+ read-only Additional Work) on the left, and
 * Total Hours / Sales Rep / Job Address / Contact / Dimensions on the right. Owns the inline-editing
 * model for the three editable fields — only one is open at a time, clicking another commits the open
 * one and re-points on the first click, and everything saves on blur.
 */
const WorkOrderSummaryNext = React.forwardRef<WorkOrderSummaryNextHandle>(function WorkOrderSummaryNext(_props, ref) {
  const classes = useStyles()
  // Phones render the same content as one stack of divided label-over-value rows (see the styles), which
  // also moves the note glyphs and hoists Total hours to the top.
  const isMobile = useMediaQuery('xs')
  const { quote, isEditable, isNoteEditable, updateQuote } = useQuote()
  const { options } = useClientOptions()
  const { userPictures, users } = useUsers()
  const { __ } = useTranslation()

  // A single "active editor" so only one inline field is open at a time. Clicking another field
  // commits the open one and re-points to the new one on the *first* click (see openEditor). The ref
  // mirrors the state for use inside blur handlers that fire before a re-render.
  type ActiveEditor = 'crew' | 'products' | 'additionalProducts' | 'hours' | null
  const [activeEditor, setActiveEditorState] = useState<ActiveEditor>(null)
  const activeEditorRef = useRef<ActiveEditor>(null)
  const switchingRef = useRef(false)
  const [hoursDraft, setHoursDraft] = useState('')
  const crewEditorRef = useRef<any>(null)
  const productEditorRef = useRef<any>(null)
  const additionalProductEditorRef = useRef<any>(null)

  const setActive = (next: ActiveEditor) => {
    activeEditorRef.current = next
    setActiveEditorState(next)
  }

  const details = useMemo(() => getDetails({ quote, options }), [quote, options])
  const totals = useMemo(() => getTotals({ quote, options, consumer: 'crew' }), [quote, options])
  const objectLabels = getObjectLabels({ options })
  // Resolves account setting → per-quote override (getQuoteOptions precedence), so the account's
  // "hide client contact info" is the default and the eye toggle overrides it for this quote only.
  const { hideWorkOrderClientContactInfo } = getQuoteOptions({ quote, options })

  const internalNote = details.notes.internal as string
  const productNote = (
    details.notes.products.useCustom ? details.notes.products.custom : details.notes.products.default
  ) as string
  // Additional Work is overridable the same way the product description is: the builder keeps
  // `default` in step with the additional-work items, `custom` wins once an estimator edits it.
  const additionalWorkNote = (
    details.notes.additionalProducts?.useCustom
      ? details.notes.additionalProducts.custom
      : details.notes.additionalProducts?.default
  ) as string
  const [[translatedCrewNote, translatedProductNote, translatedAdditionalWorkNote]] = useTranslatedNoteContent([
    internalNote,
    productNote,
    additionalWorkNote
  ])
  const crewNoteHtml = translateStructuredWorkOrderContent(translatedCrewNote || internalNote, __)
  const productHtml = translateStructuredWorkOrderContent(translatedProductNote || productNote, __)
  // Additional Work is its own block in the new layout, but it must still be translated the same way
  // the legacy view did (it folded additional work into the product note before translating).
  const additionalWorkHtml = translateStructuredWorkOrderContent(translatedAdditionalWorkNote || additionalWorkNote, __)

  const hasCrewNote = !isEmpty(internalNote)
  const hasProducts = !isEmpty(productNote)
  const hasAdditionalWork = !isEmpty(additionalWorkNote)
  const noteEditable = isEditable || isNoteEditable

  const totalHoursRaw = details.totals?.hours?.useCustom ? details.totals.hours.custom : details.totals.hours.default
  const totalHours = typeof totalHoursRaw === 'string' ? parseFloat(totalHoursRaw) : totalHoursRaw
  const hoursAdjusted = !!details.totals?.hours?.useCustom
  const defaultHours = details.totals?.hours?.default ?? 0

  // --- inline-editable Total hours ---
  // Committing writes a custom total-hours override through `setDetails` (the same path the crew-note
  // dialog uses). Clearing it reverts to the computed default. A no-op guard avoids marking the quote
  // dirty when the value hasn't changed. State (open/close) is owned by the shared active-editor model.
  const commitHoursValue = () => {
    const trimmed = hoursDraft.trim()
    const parsed = parseFloat(trimmed)
    const currentDetails = getDetails({ quote, options })
    const currentHours = currentDetails.totals.hours
    // An override that matches the computed total isn't an override: clear it instead. That's what
    // keeps opening the field and clicking away — or typing the default back in — from flagging the
    // quote as Adjusted. Compared loosely since both sides can carry float noise.
    const matchesDefault = Math.abs(parsed - Number(currentHours.default ?? 0)) < 0.005
    const revert = trimmed === '' || isNaN(parsed) || parsed < 0 || matchesDefault
    const changed = revert
      ? !!currentHours.useCustom
      : !currentHours.useCustom || Number(currentHours.custom) !== parsed
    if (!changed) {
      return
    }
    const nextHours = revert
      ? { ...currentHours, useCustom: false }
      : { ...currentHours, custom: parsed, useCustom: true }
    updateQuote({
      quote: setDetails({
        quote,
        options,
        details: { ...currentDetails, totals: { ...currentDetails.totals, hours: nextHours } }
      })
    })
  }

  // Suppress the outgoing field's blur handler (it fires as the field unmounts) for one tick, so it
  // can't re-commit a draft we're discarding or close a field we've just opened.
  const suppressBlurForATick = () => {
    switchingRef.current = true
    window.setTimeout(() => {
      switchingRef.current = false
    }, 0)
  }

  // Revert a manually-adjusted total back to the computed default.
  const resetHours = () => {
    suppressBlurForATick()
    setActive(null)
    const current = getDetails({ quote, options })
    updateQuote({
      quote: setDetails({
        quote,
        options,
        details: {
          ...current,
          totals: { ...current.totals, hours: { ...current.totals.hours, useCustom: false } }
        }
      })
    })
  }

  const owner = quote?.owner
  const estimatorName = owner ? formatName(owner) : null

  const ownerUser = (users ?? []).find((u) => u.user_id.replace(/^auth0\|/, '') === owner?.id)

  const jobAddress = getJobAddress({ quote })
  const jobAddressLines =
    jobAddress && (jobAddress.address || jobAddress.city || jobAddress.province || jobAddress.postal)
      ? addressFormat({
          address: jobAddress.address ?? '',
          city: jobAddress.city ?? '',
          subdivision: jobAddress.province ?? '',
          postalCode: jobAddress.postal ?? '',
          countryCode: 'US'
        })
      : []

  const contact = quote?.contact
  const contactName = contact ? formatName(contact) : null
  const contactPhone = contact?.phone_number
    ? formatPhoneNumber({ options, phoneNumber: contact.phone_number, extension: contact.phone_ext })
    : null
  const contactDetailsHidden = !!hideWorkOrderClientContactInfo
  const hasContactDetails = !!(contact?.email || contactPhone)

  // Per-quote override of the account's "hide client contact info" setting.
  const setContactDetailsHidden = (hidden: boolean) => {
    const next = getQuoteOptions({ options, quote })
    next.hideWorkOrderClientContactInfo = hidden
    updateQuote({ quote: setQuoteOptions({ quoteOptions: next, quote }) })
  }

  const dimensions = [
    { label: __('Walls'), value: totals.dimensions?.sqftWalls },
    { label: __('Ceiling'), value: totals.dimensions?.sqftCeiling },
    { label: __('Floor'), value: totals.dimensions?.sqftFloor },
    { label: objectLabels.unit.abbreviation.square, value: totals.dimensions?.sqft },
    { label: objectLabels.unit.abbreviation.linear, value: totals.dimensions?.lnft }
  ].filter((d) => d.value && d.value > 0)

  // Treat empty/whitespace-only HTML variants as empty so a no-op edit doesn't mark the quote dirty.
  const normalizeHtml = (html?: string | null): string => {
    const trimmed = (html ?? '').trim()
    return trimmed === '' || trimmed === '<p></p>' || trimmed === '<p><br></p>' || trimmed === '<br>' ? '' : trimmed
  }

  // Commit the inline Crew Note editor into the quote (only when it actually changed). Persists via
  // the same builder path the dialog used: details.notes.internal.
  const commitCrewNote = () => {
    const html = crewEditorRef.current?.getHTML?.()
    if (html == null) {
      return
    }
    const current = getDetails({ quote, options })
    if (normalizeHtml(html) === normalizeHtml(current.notes.internal as string)) {
      return
    }
    updateQuote({
      quote: setDetails({ quote, options, details: { ...current, notes: { ...current.notes, internal: html } } })
    })
  }

  // Apply an inline product editor's content as that note's `custom` value onto `baseQuote`, or return
  // null when there's nothing to save. Split out from the commit wrappers so a caller that also
  // changes the quote can fold both into one update instead of firing two off the same stale quote.
  // `which` picks the note: the product description or the Additional Work one — same shape, same
  // override semantics, different key.
  const buildProductNoteCommit = (which: 'products' | 'additionalProducts', baseQuote: QuoteDocument) => {
    const editorRef = which === 'products' ? productEditorRef : additionalProductEditorRef
    const html = editorRef.current?.getHTML?.()
    if (html == null) {
      return null
    }
    const current = getDetails({ quote: baseQuote, options })
    const note = current.notes[which]
    const effective = (note?.useCustom ? note.custom : note?.default) as string
    if (normalizeHtml(html) === normalizeHtml(effective)) {
      return null
    }
    return setDetails({
      quote: baseQuote,
      options,
      details: { ...current, notes: { ...current.notes, [which]: { ...note, custom: html, useCustom: true } } }
    })
  }

  const commitProductNote = (which: 'products' | 'additionalProducts') => {
    const next = buildProductNoteCommit(which, quote)
    if (next) {
      updateQuote({ quote: next })
    }
  }

  React.useImperativeHandle(ref, () => ({
    // Covers both product editors: "Hide all products" unmounts the block that holds them.
    commitOpenProducts: (baseQuote: QuoteDocument) => {
      const open = activeEditorRef.current
      if (open !== 'products' && open !== 'additionalProducts') {
        return null
      }
      const next = buildProductNoteCommit(open, baseQuote)
      setActive(null)
      return next
    }
  }))

  // Reset a product note back to its auto-generated default (useCustom: false).
  const resetProductNote = (which: 'products' | 'additionalProducts') => {
    const current = getDetails({ quote, options })
    updateQuote({
      quote: setDetails({
        quote,
        options,
        details: { ...current, notes: { ...current.notes, [which]: { ...current.notes[which], useCustom: false } } }
      })
    })
    setActive(null)
  }

  // Commit whichever editor is passed, without touching open/close state.
  const commitEditor = (which: ActiveEditor) => {
    if (which === 'crew') {
      commitCrewNote()
    } else if (which === 'products' || which === 'additionalProducts') {
      commitProductNote(which)
    } else if (which === 'hours') {
      commitHoursValue()
    }
  }

  // Open a field for inline editing. Called on **mousedown** so it runs before the currently-open
  // field's blur + the layout shift from closing it — that's what makes retargeting land on the first
  // click. We commit the previously-open field, then switch.
  const openEditor = (target: Exclude<ActiveEditor, null>) => {
    if (target === 'hours' ? !isEditable : !noteEditable) {
      return
    }
    const current = activeEditorRef.current
    if (current === target) {
      return
    }
    suppressBlurForATick()
    if (current) {
      commitEditor(current)
    }
    if (target === 'hours') {
      setHoursDraft(String(totalHours || 0))
    }
    setActive(target)
  }

  // Prevent the default focus-steal so the currently-focused editor doesn't blur before openEditor
  // runs, then open the target.
  const handleOpenMouseDown = (target: Exclude<ActiveEditor, null>) => (event: React.MouseEvent) => {
    event.preventDefault()
    openEditor(target)
  }
  const openKeyDown = (target: Exclude<ActiveEditor, null>) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openEditor(target)
    }
  }

  // Commit on blur — but not when focus stays within the same editor (toolbar / Reset button), and
  // not mid-switch (openEditor is already handling the commit + re-point).
  const handleEditorBlur = (which: Exclude<ActiveEditor, null>) => (event: React.FocusEvent<HTMLElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return
    }
    if (switchingRef.current) {
      return
    }
    commitEditor(which)
    if (activeEditorRef.current === which) {
      setActive(null)
    }
  }

  // Escape cancels the hours edit without committing.
  const cancelHours = () => {
    suppressBlurForATick()
    setActive(null)
  }

  // An empty note reads as "+ Add Crew Note" while it's editable (clicking anywhere in the block
  // opens the editor), and as an em dash when it isn't.
  const emptyNote = (label: string) =>
    noteEditable ? (
      <span className={classes.addNote}>
        <AddIcon className={classes.addNoteIcon} />
        {label}
      </span>
    ) : (
      '—'
    )

  // A note's displayed body. On phones the block's glyph moves off the label and in beside the text, so
  // every row of the mobile stack reads the same way (muted label, then icon + value); on desktop the
  // glyph stays in the label row and this is the content as-is.
  const noteBody = (Icon: React.ComponentType<{ className?: string }>, content: React.ReactNode) =>
    isMobile ? (
      <span className={classes.mobileValueRow}>
        <Icon className={classes.mobileValueIcon} />
        <span className={classes.mobileValueText}>{content}</span>
      </span>
    ) : (
      content
    )

  // The rich-text editors don't reliably self-focus on mount (the trigger's mousedown preventDefault
  // + the swap timing beats TipTap's declarative autofocus), so put the caret in the editor
  // imperatively once it has mounted. A rAF gives TipTap a tick to initialize before we focus it.
  useEffect(() => {
    const editorRefs = {
      crew: crewEditorRef,
      products: productEditorRef,
      additionalProducts: additionalProductEditorRef
    }
    const editorRef = activeEditor ? editorRefs[activeEditor] : undefined
    if (!editorRef) {
      return
    }
    const id = window.requestAnimationFrame(() => editorRef.current?.focus?.('end'))
    return () => window.cancelAnimationFrame(id)
  }, [activeEditor])

  // Total hours. Hoisted into a variable because the mobile stack leads with it (above the notes) while
  // the desktop layout keeps it at the top of the right column.
  const hoursRow = (
    <div className={classnames(classes.infoRow, classes.hoursRow)}>
      <Typography className={classes.infoLabel}>{__('Total hours')}</Typography>
      <div className={classes.hoursValue}>
        {activeEditor === 'hours' ? (
          <input
            className={classes.hoursInput}
            type="text"
            inputMode="decimal"
            autoFocus
            value={hoursDraft}
            /* One `ch` per character (tabular digits) + a pixel for the caret, so swapping the
                   text for this field doesn't nudge the "hrs" label sitting beside it. */
            style={{ width: `calc(${Math.max(1, hoursDraft.length)}ch + 1px)` }}
            onChange={(ev) => setHoursDraft(ev.target.value)}
            onFocus={(ev) => ev.target.select()}
            onBlur={handleEditorBlur('hours')}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter') {
                ev.preventDefault()
                ev.currentTarget.blur()
              } else if (ev.key === 'Escape') {
                ev.preventDefault()
                cancelHours()
              }
            }}
          />
        ) : (
          <span
            className={classnames(classes.hoursNumber, { [classes.hoursClickable]: isEditable })}
            onMouseDown={isEditable ? handleOpenMouseDown('hours') : undefined}
            role={isEditable ? 'button' : undefined}
            tabIndex={isEditable ? 0 : undefined}
            onKeyDown={isEditable ? openKeyDown('hours') : undefined}
          >
            {totalHours || 0}
          </span>
        )}
        <span className={classes.hoursUnit}>{__('hrs')}</span>
      </div>
      {isEditable && hoursAdjusted && (
        <div className={classes.adjustedPill}>
          <span className={classes.adjustedLabel}>{__('Adjusted')}</span>
          {/* preventDefault keeps the open hours input focused so it can't blur-commit before onClick */}
          <button
            type="button"
            className={classes.adjustedResetButton}
            onMouseDown={(ev) => ev.preventDefault()}
            onClick={resetHours}
            title={`${__('Reset to')} ${defaultHours} ${__('hrs')}`}
          >
            <ArrowRotateLeftIcon className={classes.adjustedResetIcon} />
            {__('Reset')}
          </button>
        </div>
      )}
    </div>
  )

  return (
    <div className={classes.root}>
      {isMobile && hoursRow}
      <div className={classes.left}>
        {/* Crew Note — click the text to edit inline; saves on blur. */}
        <div
          className={classnames(classes.block, {
            [classes.blockClickable]: noteEditable && activeEditor !== 'crew'
          })}
          onMouseDown={noteEditable && activeEditor !== 'crew' ? handleOpenMouseDown('crew') : undefined}
          onKeyDown={noteEditable && activeEditor !== 'crew' ? openKeyDown('crew') : undefined}
          role={noteEditable && activeEditor !== 'crew' ? 'button' : undefined}
          tabIndex={noteEditable && activeEditor !== 'crew' ? 0 : undefined}
        >
          <div className={classes.blockLabelRow}>
            {!isMobile && <StickyNoteIcon className={classes.blockIcon} />}
            <Typography className={classes.blockLabel}>{__('Crew Note')}</Typography>
          </div>
          {activeEditor === 'crew' ? (
            <div className={classes.editorWrap} onBlur={handleEditorBlur('crew')}>
              <Editor ref={crewEditorRef} content={internalNote || ''} fullWidth autofocus="end" />
            </div>
          ) : (
            <Typography component="div" className={classes.noteText}>
              {noteBody(
                StickyNoteIcon,
                hasCrewNote ? <HtmlContent content={crewNoteHtml} /> : emptyNote(__('Add Crew Note'))
              )}
            </Typography>
          )}
        </div>

        {/* Products + Additional Work — two independent notes kept close together as one group.
            Each is its own click target with its own hover. The nav bar's "Hide all products" hides
            only the cards' product lines — this description stays, since it is the work order's
            headline copy. */}
        <div className={classes.productsGroup}>
          <div
            className={classnames(classes.block, {
              [classes.blockClickable]: noteEditable && activeEditor !== 'products'
            })}
            onMouseDown={noteEditable && activeEditor !== 'products' ? handleOpenMouseDown('products') : undefined}
            onKeyDown={noteEditable && activeEditor !== 'products' ? openKeyDown('products') : undefined}
            role={noteEditable && activeEditor !== 'products' ? 'button' : undefined}
            tabIndex={noteEditable && activeEditor !== 'products' ? 0 : undefined}
          >
            <div className={classes.blockLabelRow}>
              {!isMobile && <AlignLeftIcon className={classes.blockIcon} />}
              <Typography className={classes.blockLabel}>{__('Product Description')}</Typography>
            </div>
            {activeEditor === 'products' ? (
              <div className={classes.editorWrap} onBlur={handleEditorBlur('products')}>
                <Button
                  className={classes.resetButton}
                  variant="text"
                  size="small"
                  onClick={() => resetProductNote('products')}
                >
                  {__('Reset')}
                </Button>
                <Editor ref={productEditorRef} content={productNote || ''} fullWidth autofocus="end" />
              </div>
            ) : (
              <Typography component="div" className={classes.noteText}>
                {noteBody(
                  AlignLeftIcon,
                  hasProducts ? <HtmlContent content={productHtml} /> : emptyNote(__('Add Product Description'))
                )}
              </Typography>
            )}
          </div>

          {(hasAdditionalWork || activeEditor === 'additionalProducts') && (
            <div
              className={classnames(classes.block, {
                [classes.blockClickable]: noteEditable && activeEditor !== 'additionalProducts'
              })}
              onMouseDown={
                noteEditable && activeEditor !== 'additionalProducts'
                  ? handleOpenMouseDown('additionalProducts')
                  : undefined
              }
              onKeyDown={
                noteEditable && activeEditor !== 'additionalProducts' ? openKeyDown('additionalProducts') : undefined
              }
              role={noteEditable && activeEditor !== 'additionalProducts' ? 'button' : undefined}
              tabIndex={noteEditable && activeEditor !== 'additionalProducts' ? 0 : undefined}
            >
              {/* No icon — the align-left glyph belongs to Product Description; this reads as its
              sub-note, so it's a plain labelled row. */}
              <div className={classes.blockLabelRow}>
                <Typography className={classes.blockLabel}>{`${__(objectLabels.additionalWork.value)}:`}</Typography>
              </div>
              {activeEditor === 'additionalProducts' ? (
                <div className={classes.editorWrap} onBlur={handleEditorBlur('additionalProducts')}>
                  <Button
                    className={classes.resetButton}
                    variant="text"
                    size="small"
                    onClick={() => resetProductNote('additionalProducts')}
                  >
                    {__('Reset')}
                  </Button>
                  <Editor
                    ref={additionalProductEditorRef}
                    content={additionalWorkNote || ''}
                    fullWidth
                    autofocus="end"
                  />
                </div>
              ) : (
                <Typography component="div" className={classes.noteText}>
                  <HtmlContent content={additionalWorkHtml} />
                </Typography>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={classes.vDivider} />

      <div className={classes.right}>
        {!isMobile && hoursRow}

        {/* The group rules are redundant on phones — every row draws its own top border there. Not
            rendered at all rather than hidden by CSS, which MUI's own Divider styles can win against. */}
        {!isMobile && <Divider />}

        <div className={classes.infoRow}>
          <Typography className={classes.infoLabel}>{__('Sales Rep')}</Typography>
          <div className={classnames(classes.infoValue, classes.salesRepName)}>
            <span className={classes.infoValueRow}>
              {owner && (
                <EstimatorAvatar
                  className={classes.avatar}
                  user={{ id: owner.id, firstName: owner.firstName, lastName: owner.lastName }}
                  userPictures={userPictures}
                  width="16px"
                  height="16px"
                  renderEmpty="placeholder"
                  avatarColor={getActivityColor(ownerUser?.user_metadata?.color)}
                />
              )}
              {estimatorName || '—'}
            </span>
          </div>
        </div>

        <div className={classnames(classes.infoRow, classes.jobAddressRow)}>
          <Typography className={classes.infoLabel}>{__('Job Address')}</Typography>
          <div className={classes.infoValue}>
            <span className={classes.infoValueRow}>
              <LocationOnIcon className={classes.infoIcon} />
              <span>{jobAddressLines.length > 0 ? jobAddressLines.join(', ') : '—'}</span>
            </span>
          </div>
        </div>

        <div className={classes.infoRow}>
          <Typography className={classes.infoLabel}>{__('Contact')}</Typography>
          <div className={classnames(classes.infoValue, classes.contactBlock)}>
            <div className={classes.contactHeaderRow}>
              <span className={classes.infoValueRow}>
                <PersonIcon className={classes.infoIcon} />
                <span>{contactName || '—'}</span>
              </span>
              {isEditable && hasContactDetails && !contactDetailsHidden && (
                <button
                  type="button"
                  className={classes.contactHideButton}
                  title={__('Hide email & phone')}
                  aria-label={__('Hide email & phone')}
                  onClick={() => setContactDetailsHidden(true)}
                >
                  <VisibilityOffIcon />
                </button>
              )}
            </div>
            {contactDetailsHidden ? (
              hasContactDetails &&
              (isEditable ? (
                <button
                  type="button"
                  className={classes.contactHidden}
                  title={__('Show email & phone')}
                  onClick={() => setContactDetailsHidden(false)}
                >
                  {__('Hidden')}
                </button>
              ) : (
                <span className={classes.contactHiddenStatic}>{__('Hidden')}</span>
              ))
            ) : (
              <>
                {contact?.email && <span className={classes.contactLine}>{contact.email}</span>}
                {contactPhone && <span className={classes.contactLine}>{contactPhone}</span>}
              </>
            )}
          </div>
        </div>

        {dimensions.length > 0 && (
          <>
            {!isMobile && <Divider />}
            <div className={classes.infoRow}>
              <Typography className={classes.infoLabel}>{__('Dimensions')}</Typography>
              <div className={classes.dimensions}>
                <SquareFootIcon className={classes.infoIcon} />
                {dimensions.map((d, index) => (
                  <React.Fragment key={d.label}>
                    {index > 0 && <span className={classes.dimensionDivider} />}
                    <span className={classes.dimensionItem}>
                      <span className={classes.dimensionLabel}>{d.label}</span>
                      <span className={classes.dimensionValue}>{d.value.toLocaleString()}</span>
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
})

export default WorkOrderSummaryNext
